import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@duck/ui'
import { useRef, useState } from 'react'
import { useTar } from '@/modules'
import { trackFaq } from '@/modules/faqAnalytics'
import { StructuredDataScript } from '@/modules/seo'
import { ExternalURLs, ORGANIZATION_EMAIL } from '@/types/externalURLs'

const SITE_URL = ExternalURLs.Site

type EducationFaqItem = {
  slug: string
  question: string
  answer: string[]
  bullets?: string[]
  cta?: boolean
}

type EducationFaqSection = {
  id: string
  number: string
  title: string
  description: string
  items: EducationFaqItem[]
}

function EducationSectionControls({
  section,
  openItems,
  setOpenItems,
  t,
}: {
  section: EducationFaqSection
  openItems: string[]
  setOpenItems: (updater: (current: string[]) => string[]) => void
  t: ReturnType<typeof useTar>['t']
}) {
  const sectionSlugs = section.items.map((item) => item.slug)
  const allOpen = sectionSlugs.every((slug) => openItems.includes(slug))

  return (
    <div className="flex justify-end gap-3 text-sm">
      <button
        type="button"
        className="text-muted-foreground transition-colors hover:text-foreground"
        onClick={() =>
          setOpenItems((current) => [
            ...current.filter((slug) => !sectionSlugs.includes(slug)),
            ...sectionSlugs,
          ])
        }
      >
        {allOpen ? t('faq.allQuestionsOpen') : t('faq.expandAll')}
      </button>
      <button
        type="button"
        className="text-muted-foreground transition-colors hover:text-foreground"
        onClick={() =>
          setOpenItems((current) =>
            current.filter((slug) => !sectionSlugs.includes(slug))
          )
        }
      >
        {t('faq.collapseAll')}
      </button>
    </div>
  )
}

export function EducationPageStructured() {
  const { t, locale } = useTar()
  const faqSections = t('educationFaq.sections', {
    returnObjects: true,
  }) as unknown as EducationFaqSection[]
  const [openItems, setOpenItems] = useState<string[]>([])
  const openItemsRef = useRef(openItems)
  openItemsRef.current = openItems

  const trackEducationFaq = (
    event: Parameters<typeof trackFaq>[0],
    payload: Parameters<typeof trackFaq>[1]
  ) => trackFaq(event, payload)

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: locale,
    mainEntity: faqSections
      .flatMap((section) => section.items)
      .map((item) => ({
        '@type': 'Question',
        name: item.question,
        url: new URL(`/for-education#${item.slug}`, SITE_URL).toString(),
        acceptedAnswer: {
          '@type': 'Answer',
          text: [...item.answer, ...(item.bullets ?? [])].join(' '),
        },
      })),
  }

  return (
    <>
      <StructuredDataScript
        id="education-faq-structured-data"
        data={structuredData}
      />
      <div className="flex flex-1 w-full justify-center">
        <main
          className="flex-1 min-w-0 max-w-5xl space-y-10 md:space-y-12 mt-4 md:mt-8 mb-16 px-6 md:px-10"
        >
          <section
            className="py-8 md:py-12"
            aria-labelledby="education-heading"
          >
            <div className="max-w-3xl space-y-6">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">
                {t('education.eyebrow')}
              </p>
              <h1
                id="education-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-foreground"
              >
                {t('education.title')}
                <span className="text-accent">.</span>
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('education.introduction')}
              </p>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
                {t('education.subtitle')}
              </p>
            </div>
          </section>

          <section className="px-0" aria-label={t('educationFaq.ariaLabel')}>
            <div className="rounded-xl border border-border bg-card/50 p-5 md:p-8 space-y-10">
              {faqSections.map((section) => (
                <section
                  key={section.id}
                  id={section.id}
                  className="scroll-mt-24 grid gap-5 md:grid-cols-[minmax(0,0.7fr)_minmax(0,1.5fr)]"
                  aria-labelledby={`${section.id}-heading`}
                >
                  <div className="space-y-3">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-accent">
                      {section.number}
                    </p>
                    <h2
                      id={`${section.id}-heading`}
                      className="text-2xl md:text-3xl font-bold text-foreground tracking-tight"
                    >
                      {section.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {section.description}
                    </p>
                  </div>
                  <div className="space-y-4">
                    <EducationSectionControls
                      section={section}
                      openItems={openItems}
                      setOpenItems={setOpenItems}
                      t={t}
                    />
                    <Accordion
                      type="multiple"
                      className="w-full"
                      value={section.items
                        .filter((item) => openItems.includes(item.slug))
                        .map((item) => item.slug)}
                      onValueChange={(next) => {
                        const previous = openItemsRef.current
                        next.forEach((slug) => {
                          if (!previous.includes(slug))
                            trackEducationFaq('faq:item:open', {
                              slug,
                              sectionId: section.id,
                              trigger: 'user',
                            })
                        })
                        previous
                          .filter(
                            (slug) =>
                              section.items.some(
                                (item) => item.slug === slug
                              ) && !next.includes(slug)
                          )
                          .forEach((slug) =>
                            trackEducationFaq('faq:item:close', {
                              slug,
                              sectionId: section.id,
                            })
                          )
                        setOpenItems((current) => [
                          ...current.filter(
                            (slug) =>
                              !section.items.some((item) => item.slug === slug)
                          ),
                          ...next,
                        ])
                      }}
                    >
                      {section.items.map((item) => (
                        <AccordionItem
                          key={item.slug}
                          value={item.slug}
                          id={item.slug}
                          className="scroll-mt-24"
                        >
                          <AccordionTrigger>
                            {item.question}
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-4">
                              {item.answer.map((paragraph) => (
                                <p key={paragraph}>{paragraph}</p>
                              ))}
                              {item.bullets ? (
                                <ul className="list-disc space-y-2 ms-6">
                                  {item.bullets.map((bullet) => (
                                    <li key={bullet}>{bullet}</li>
                                  ))}
                                </ul>
                              ) : null}
                              {item.cta && (
                                <a
                                  href={`mailto:${ORGANIZATION_EMAIL}`}
                                  onClick={() =>
                                    trackEducationFaq(
                                      'faq:contact:email_click',
                                      {
                                        slug: item.slug,
                                      }
                                    )
                                  }
                                  className="inline-flex items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-semibold text-accent-foreground shadow-[0_0_24px_-8px_hsl(var(--color-accent)/0.55)] transition-all hover:-translate-y-0.5 hover:bg-accent/85"
                                >
                                  {t('education.contactCta')}
                                </a>
                              )}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </div>
                </section>
              ))}
            </div>
          </section>
        </main>
      </div>
    </>
  )
}
